// Console navigation: grouped sections (the xenquote / enforza-neon side nav), with the
// screens from the clickable prototype (00-initial-commercial-intelligence/portal).
// Drives the sidebar, the routes and the breadcrumb labels.
import { Building2, CreditCard, PoundSterling, Crosshair, LayoutDashboard, Map, Search, SlidersHorizontal, UserRound, Users, type LucideIcon } from "lucide-react";

export interface NavItem {
  path: string; // route path without the leading slash
  label: string;
  icon: LucideIcon;
  blurb: string; // one-line description for placeholder pages
  soon?: boolean; // shown greyed out with a "Soon" chip, not clickable
  custom?: boolean; // has its own page (not the generic placeholder)
  /** Highlight the row for these paths too (default: its own path only) */
  activeFor?: (pathname: string) => boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    label: "Overview",
    items: [{ path: "dashboard", label: "Dashboard", icon: LayoutDashboard, blurb: "Report credits, properties you hold and reports on their way.", custom: true }],
  },
  {
    // The core loop: shortlist → pack → building (PORTAL-WORKFLOW.md steps 3–9)
    label: "Properties",
    items: [
      {
        path: "find",
        label: "Search properties",
        icon: Search,
        blurb: "An anonymised shortlist of properties in your area that use a lot of electricity and have no solar.",
        custom: true,
      },
      // A property the customer names themselves, anywhere (#36, #37): right under Find
      {
        path: "targets/request",
        label: "Request a property",
        icon: Crosshair,
        blurb: "Search for a property by business name or address and request its report.",
        custom: true,
      },
      // Each building's pack progress (ordered → in progress → completed) shows here too
      {
        path: "properties",
        label: "My properties",
        icon: Building2,
        blurb: "Properties you've ordered, with their reports and status.",
        custom: true,
        activeFor: (p) => p === "/properties" || p.startsWith("/properties/"),
      },
    ],
  },
  {
    // How the company works: where it works, then its pricing models (Neil, 11 Oct 2026)
    label: "Your business",
    items: [
      { path: "coverage", label: "Coverage", icon: Map, blurb: "The Local Authority Districts you work in.", custom: true },
      { path: "filters", label: "Filters", icon: SlidersHorizontal, blurb: "Which properties you want to see.", custom: true },
      // The company's own prices and assumptions, used in its reports (#38)
      { path: "pricing", label: "Pricing models", icon: PoundSterling, blurb: "Your prices and assumptions, used in your reports.", custom: true },
    ],
  },
  {
    label: "Account",
    items: [
      { path: "profile", label: "Profile", icon: UserRound, blurb: "Your company's details.", custom: true },
      { path: "users", label: "Users", icon: Users, blurb: "Who can sign in: invite people, set roles.", custom: true },
      { path: "billing", label: "Billing", icon: CreditCard, blurb: "Your plan, report credits and invoices." },
    ],
  },
];

/** Every nav item, flattened (for routes and breadcrumb labels). */
export const navItems: NavItem[] = navSections.flatMap((s) => s.items);
